import React, { useState, useEffect, useCallback } from 'react';
import {
  Input,
  Spinner,
  makeStyles,
  tokens,
  Text
} from '@fluentui/react-components';
import { SearchRegular, PeopleTeamRegular } from '@fluentui/react-icons';
import { groupService, EntraGroup } from '../services/groupService';

interface GroupAutocompleteProps {
  value?: string;
  onChange?: (selectedGroup: EntraGroup | null, displayName: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const useStyles = makeStyles({
  container: {
    position: 'relative',
    width: '100%'
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  input: {
    width: '100%'
  },
  loadingIndicator: {
    position: 'absolute',
    right: tokens.spacingHorizontalM,
    pointerEvents: 'none'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    zIndex: 1000,
    maxHeight: '200px',
    overflowY: 'auto'
  },
  dropdownItem: {
    width: '100%',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover
    },
    '&:focus': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      outline: `2px solid ${tokens.colorBrandBackground}`,
      outlineOffset: '-2px'
    },
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  groupInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS
  },
  groupName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200
  },
  groupDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    marginTop: '2px'
  },
  groupType: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic'
  },
  noResults: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200
  },
  errorMessage: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200
  }
});

export const GroupAutocomplete: React.FC<GroupAutocompleteProps> = ({
  value = '',
  onChange,
  placeholder = '(Entra group lookup)',
  disabled = false
}) => {
  const styles = useStyles();
  const [inputValue, setInputValue] = useState(value);
  const [groups, setGroups] = useState<EntraGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<EntraGroup | null>(null);
  const [hasUserTyped, setHasUserTyped] = useState(false);

  // Update input value when prop changes (for loading existing data)
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
      setHasUserTyped(false);  // Reset when value changes from parent
      setGroups([]);  // Clear any previous search results
      setShowDropdown(false);
    }
  }, [value]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.trim().length < 2) {
        setGroups([]);
        setShowDropdown(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await groupService.searchGroups(query);
        console.log('Group search result:', result);
        console.log('Groups to set:', result.groups);
        console.log('Groups length:', result.groups?.length);
        
        // Filter out groups that are already in use (isInUse: true)
        const groupsToSet = (result.groups || []).filter(group => {
          // Only filter groups (not users), and exclude those with isInUse: true
          if (group.type === 'group' && group.isInUse === true) {
            return false;
          }
          return true;
        });
        
        console.log('>>>result.groups', result.groups)
        console.log('Filtered groups (excluding isInUse):', groupsToSet);
        setGroups(groupsToSet);
        setShowDropdown(groupsToSet.length > 0);
        console.log('Setting showDropdown to:', groupsToSet.length > 0);
      } catch (err) {
        console.error('Group search error:', err);
        setError(err instanceof Error ? err.message : 'Search failed');
        setGroups([]);
        setShowDropdown(true);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Only search when user actually types, not on initial value set
  useEffect(() => {
    if (hasUserTyped) {
      debouncedSearch(inputValue);
    }
  }, [inputValue, debouncedSearch, hasUserTyped]);

  const handleInputChange = (value: string) => {
    setHasUserTyped(true);  // Mark that user has typed
    setInputValue(value);
    setSelectedGroup(null);
    onChange?.(null, value);
  };

  const handleGroupSelect = (group: EntraGroup) => {
    setInputValue(group.displayName);
    setSelectedGroup(group);
    setShowDropdown(false);
    onChange?.(group, group.displayName);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleInputFocus = () => {
    if (groups.length > 0) {
      setShowDropdown(true);
    }
  };

  // Debug logging
  console.log('Render - groups:', groups);
  console.log('Render - showDropdown:', showDropdown);
  console.log('Render - groups.length:', groups.length);
  console.log('Render - inputValue:', inputValue);

  return (
    <div className={styles.container} data-testid="group-autocomplete">
      <div className={styles.inputContainer} data-testid="group-autocomplete-input-container">
        <Input
          className={styles.input}
          value={inputValue}
          onChange={(_, data) => handleInputChange(data.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          contentBefore={<SearchRegular />}
          data-testid="group-autocomplete-input"
        />
        {loading && (
          <div className={styles.loadingIndicator} data-testid="group-autocomplete-loading-indicator">
            <Spinner size="tiny" data-testid="group-autocomplete-loading-spinner" />
          </div>
        )}
      </div>

      {showDropdown && (
        <div className={styles.dropdown} data-testid="group-autocomplete-dropdown">
          {error ? (
            <div className={styles.errorMessage} data-testid="group-autocomplete-error">
              {error}
            </div>
          ) : groups.length > 0 ? (
            groups.map((group, index) => (
              <button
                key={group.id}
                className={styles.dropdownItem}
                onClick={() => handleGroupSelect(group)}
                type="button"
                aria-label={`Select group ${group.displayName}`}
                data-testid={`group-autocomplete-option-${index}`}
              >
                <div className={styles.groupInfo} data-testid="group-autocomplete-group-info">
                  <PeopleTeamRegular data-testid="group-autocomplete-group-icon" />
                  <div data-testid="group-autocomplete-group-meta">
                    <div className={styles.groupName} data-testid="group-autocomplete-group-name">
                      {group.displayName}
                    </div>
                    {group.description && (
                      <div className={styles.groupDescription} data-testid="group-autocomplete-group-description">
                        {group.description}
                      </div>
                    )}
                    <div className={styles.groupType} data-testid="group-autocomplete-group-type">
                      {group.subType || 'Group'}
                    </div>
                  </div>
                </div>
              </button>
            ))
          ) : inputValue.trim().length >= 2 ? (
            <div className={styles.noResults} data-testid="group-autocomplete-no-results">
              No groups found for "{inputValue}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Utility function for debouncing
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default GroupAutocomplete;